import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useFormik } from 'formik';
import { Link } from 'react-router-dom';
import Loading from '../components/Loading';
import Input from '../components/form/Input';
import Layout from './Layout';
import { useSelector } from 'react-redux';

export const getProviders = (query = {}) => {
    return axios
        .request({
            url: `${process.env.REACT_APP_API_URL}/authorized/providers`,
            method: 'GET',
            params: query
        })
        .then((resp) => resp.data);
};

const removeProvider = (id) => {
    let safeUsername = window.encodeURIComponent(id);
    return axios.request({
        url: `${process.env.REACT_APP_API_URL}/authorized/providers/${safeUsername}`,
        method: 'DELETE'
    });
};

const Providers = () => {
    const session = useSelector((state) => state.session);
    const [isLoading, setIsLoading] = useState(true);
    const [results, setResults] = useState([]);
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState();
    const order_by = 'name:asc';
    const perPage = 20;

    const { values, setFieldValue } = useFormik({
        initialValues: {
            search: ''
        }
    });

    const getResults = useCallback(() => {
        setIsLoading(true);
        getProviders({
            order_by: order_by,
            per_page: perPage,
            page: page,
            q: values.search || null
        })
            .then((resp) => {
                setPageCount(Math.ceil(resp.total / perPage));

                setResults(resp.results);
                setIsLoading(false);
            })
            .catch((err) => {
                setResults([]);
                setIsLoading(false);
            });
    }, [order_by, perPage, page, values.search]);

    useEffect(() => {
        getResults();
    }, [getResults, page]);

    const handleDelete = (id) => {
        removeProvider(id).then(() => getResults());
    };

    const handleSearch = (e) => {
        e.preventDefault();
        getResults();
    };

    const handleSearchChange = (id, value) => {
        setPage(1);
        setFieldValue('search', value);
        if (!value) {
            getResults();
        }
    };

    const handleSearchReset = () => {
        setFieldValue('search', '');
        setPage(1);
        getResults();
    };

    const nextPage = () => {
        setPage(page + 1);
    };

    const prevPage = () => {
        setPage(page - 1);
    };

    return (
        <Layout>
            <div>
                <div className="page-header">
                    <h1 className="collapse">Providers</h1>
                </div>

                <div className="row mb-5">
                    <div className="col-xs-12 col-sm">
                        <form onSubmit={handleSearch} onReset={handleSearchReset} noValidate>
                            <Input
                                id="search"
                                value={values.search}
                                onChange={handleSearchChange}
                                placeholder="Search Name, Publisher Name, or Vendor ID."
                                append={
                                    <>
                                        {values.search && (
                                            <button className="btn btn-sm btn-muted" type="reset">
                                                &times;
                                            </button>
                                        )}
                                        <button className="btn btn-sm btn-secondary" type="submit">
                                            Search
                                        </button>
                                    </>
                                }
                                small
                                collapse
                            />
                        </form>
                    </div>
                        <div className="col">
                            <Link to="/providers/add" className="btn btn-primary btn-sm">
                                Add Provider
                            </Link>
                        </div>
                </div>

                <table className="table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Publisher(s)</th>
                            <th>Vendor(s)</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading && (
                            <tr>
                                <td colSpan={4}>
                                    <Loading />
                                </td>
                            </tr>
                        )}
                        {!isLoading && !results.length && (
                            <tr>
                                <td colSpan={4}>No Providers</td>
                            </tr>
                        )}
                        {!isLoading &&
                            results.map((user) => (
                                <tr key={user.id}>
                                    <td>
                                        <Link to={`/providers/${user.id}`}>{user.name}</Link>
                                    </td>
                                    <td>{(user.publishers || []).join(', ')}</td>
                                    <td>{(user.vendors || []).map((v) => v.findawayvendorid).join(', ')}</td>
                                    <td>
                                        <button
                                            className="btn btn-danger btn-sm"
                                            onClick={(e) => handleDelete(user.id)}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                    </tbody>
                </table>
                {!!results.length && !isLoading && (
                    <div className="sales-pagination">
                        <button className="btn btn-sm" onClick={prevPage} disabled={page === 1}>
                            Prev
                        </button>
                        <span>
                            Page {page} of {pageCount}
                        </span>
                        <button className="btn btn-sm" onClick={nextPage} disabled={page >= pageCount}>
                            Next
                        </button>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default Providers;
